import { useId } from "react"

/**
 * Shared custom SVGs used across homepage sections (wizard progress, FAQ
 * accordion, final CTA). Match the existing visual family in
 * `hero-grass-through.tsx` and `grass-blades.tsx`:
 * stroked curved paths, round line caps, no internal motion. Color is driven
 * by `currentColor` so callers control state via `text-*` utilities.
 */

interface IconProps {
  className?: string
}

export function StepPhotoIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className={className}>
      <rect
        x="4"
        y="9"
        width="24"
        height="17"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M11 9l1.5-2.5h7L21 9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="16"
        cy="17.5"
        r="4.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M16 17.5c0-2 0-3.5 0-5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M25 6c.6 1 1 1.5 2 2-1 .6-1.5 1-2 2-.6-1-1-1.5-2-2 1-.6 1.5-1 2-2z"
        fill="currentColor"
        opacity="0.6"
      />
    </svg>
  )
}

export function StepLocationIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className={className}>
      <path
        d="M16 28c-5-6-8-10-8-15a8 8 0 1116 0c0 5-3 9-8 15z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle
        cx="16"
        cy="13"
        r="3"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M9 27c1-1 2-1.5 3-1M20 27c-1-1 0-1.5 3-1"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.6"
      />
    </svg>
  )
}

export function StepPlanIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className={className}>
      <rect
        x="5"
        y="7"
        width="22"
        height="20"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M5 13h22"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M11 4v5M21 4v5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M19 22c0-3 .5-5 2-7M21 22c0-2 0-3.5 1-5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="11" cy="18" r="0.9" fill="currentColor" />
      <circle cx="14.5" cy="18" r="0.9" fill="currentColor" opacity="0.5" />
      <circle cx="11" cy="22" r="0.9" fill="currentColor" opacity="0.5" />
    </svg>
  )
}

export function FaqLeafChevron({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path
        d="M3 6c2.5 1 4 3 5 6 1-3 2.5-5 5-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function SproutBladeIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={className}>
      <path
        d="M10 17V8c0-3 2-5 6-6-1 4-3 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 11c-3 0-5-2-6-5 4 1 6 3 6 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 18h14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
    </svg>
  )
}

export function SunGrassMotif({ className }: IconProps) {
  const gradientId = `sun-grass-blade-${useId().replace(/:/g, "")}`

  return (
    <svg viewBox="0 0 180 60" fill="none" aria-hidden="true" className={className}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="60" x2="0" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#15803d" />
          <stop offset="0.6" stopColor="#22c55e" />
          <stop offset="1" stopColor="#86efac" />
        </linearGradient>
      </defs>
      <circle cx="90" cy="20" r="10" fill="#fde68a" opacity="0.55" />
      <circle cx="90" cy="20" r="6" fill="#fbbf24" opacity="0.7" />
      <g transform="translate(0,2)">
        <path d="M28 56c0-10-2-16-6-22" stroke={`url(#${gradientId})`} strokeWidth="2.4" strokeLinecap="round" />
        <path d="M44 56c0-14-2-22-6-30" stroke={`url(#${gradientId})`} strokeWidth="2.6" strokeLinecap="round" />
        <path d="M62 56c0-12-1-20-2-28" stroke={`url(#${gradientId})`} strokeWidth="2.4" strokeLinecap="round" />
        <path d="M78 56c0-10 1-16 4-22" stroke={`url(#${gradientId})`} strokeWidth="2.2" strokeLinecap="round" />
        <path d="M102 56c0-10-1-16-4-22" stroke={`url(#${gradientId})`} strokeWidth="2.2" strokeLinecap="round" />
        <path d="M118 56c0-12 1-20 2-28" stroke={`url(#${gradientId})`} strokeWidth="2.4" strokeLinecap="round" />
        <path d="M136 56c0-14 2-22 6-30" stroke={`url(#${gradientId})`} strokeWidth="2.6" strokeLinecap="round" />
        <path d="M152 56c0-10 2-16 6-22" stroke={`url(#${gradientId})`} strokeWidth="2.4" strokeLinecap="round" />
      </g>
      <path d="M10 58h160" stroke="#0f1d14" strokeWidth="1.2" strokeLinecap="round" opacity="0.35" />
    </svg>
  )
}
